import React, { useState } from 'react';
import { Mission } from '@/lib/gamification/types';
import { supabase } from '@/lib/supabase';
import { assignMissionToAllUsers } from '@/lib/gamification/missionService'; // Descomentado
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Loader2, Users, AlertTriangle, CheckCircle } from 'lucide-react';

interface AssignMissionToAllModalProps {
  mission: Mission;
  isOpen: boolean;
  onClose: () => void;
  onAssignedToAll?: (successCount: number, failureCount: number) => void; // Adicionado failureCount
}

const AssignMissionToAllModal: React.FC<AssignMissionToAllModalProps> = ({ mission, isOpen, onClose, onAssignedToAll }) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignmentResult, setAssignmentResult] = useState<{success: number, failed: number} | null>(null);

  const handleAssignToAll = async () => {
    setIsAssigning(true);
    setError(null);
    setAssignmentResult(null);

    try {
      // Usar a função real do missionService
      const result = await assignMissionToAllUsers(supabase, mission.id, null); 

      if (result.error) {
        throw new Error(result.error);
      }
      
      const message = `Missão "${mission.title}" processada. Sucessos: ${result.successCount}, Falhas: ${result.failureCount}.`;
      setAssignmentResult({ success: result.successCount, failed: result.failureCount});

      if (result.successCount > 0 && result.failureCount === 0) {
        toast.success(message);
      } else if (result.failureCount > 0) {
        toast.error(message + " Verifique os logs do console para detalhes.");
      } else {
        // Se chegou aqui, significa que result.successCount === 0 && result.failureCount === 0
        // Isso pode acontecer se não houver usuários no sistema, por exemplo.
        // Usamos toast.success para indicar que a operação foi concluída como esperado, mesmo sem atribuições.
        toast.success(message);
      }

      if (onAssignedToAll) {
        onAssignedToAll(result.successCount, result.failureCount);
      }
      // Não fechar automaticamente, permitir que o usuário veja o resultado e feche manualmente

    } catch (err: any) {
      console.error(`Falha ao atribuir missão "${mission.title}" para todos os usuários:`, err);
      const errorMessage = err.message || 'Ocorreu um erro desconhecido ao tentar atribuir a missão a todos.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(openState) => {
      if (!openState) {
        onClose();
        // Resetar estado ao fechar se a atribuição já ocorreu
        if (assignmentResult || error) {
          setAssignmentResult(null);
          setError(null);
        }
      }
    }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-yellow-500" />
            Confirmar Atribuição em Massa
          </DialogTitle>
          <DialogDescription>
            Você está prestes a atribuir a missão "<strong>{mission.title}</strong>" (frequência: {mission.frequency}) 
            para <strong>TODOS</strong> os usuários do sistema. Esta ação pode ser demorada.
            <br />
            Tem certeza que deseja continuar?
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <div className="my-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <p><strong>Erro:</strong> {error}</p>
          </div>
        )}
        {assignmentResult && !isAssigning && (
          <div className={`my-4 p-3 border rounded-md flex items-center ${assignmentResult.failed > 0 ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-green-100 border-green-400 text-green-700'}`}>
            <CheckCircle className={`h-5 w-5 mr-2 ${assignmentResult.failed > 0 ? 'text-yellow-600' : 'text-green-600'}`}/>
            <p>
              Processamento concluído: <br />
              Sucessos: <strong>{assignmentResult.success}</strong>, Falhas: <strong>{assignmentResult.failed}</strong>.
              {assignmentResult.failed > 0 && " Verifique os logs do console para detalhes."}
            </p>
          </div>
        )}

        <DialogFooter className="sm:justify-between mt-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={() => {
                onClose();
                if (assignmentResult || error) {
                  setAssignmentResult(null);
                  setError(null);
                }
            }}>
              {assignmentResult || error ? "Fechar" : "Cancelar"}
            </Button>
          </DialogClose>
          <Button onClick={handleAssignToAll} disabled={isAssigning || !!assignmentResult} variant={assignmentResult ? "default" : "destructive"}>
            {isAssigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Atribuindo a Todos...
              </>
            ) : assignmentResult ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Concluído
              </>
            ) : (
              <>
                <Users className="mr-2 h-4 w-4" />
                Confirmar e Atribuir a Todos
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignMissionToAllModal; 